import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { AvatarList } from "@/components/canvas-editor/components/AvatarList";
import { BlockSeparator, ListAttribution } from "@/components/canvas-editor/components/Container";
import { Spinner } from "@/components/Spinner";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { getUserListQueryOptions } from "@/lib/api";
import { listUrlByDid } from "@/lib/utils";



export function ListPreview({
  listUrl,
  hasSeparator = true,
  onCanonicalUrl,
}: {
  listUrl: string;
  hasSeparator?: boolean;
  /**
   * Called with the DID form of `listUrl` when the two differ — see `listUrlByDid`.
   *
   * The preview is where the canonical AT-URI first lands, so this rewrite costs no
   * extra lookup. Optional: a read-only preview passes nothing and the rewrite is
   * skipped, because a viewer must not silently edit somebody else's feed.
   */
  onCanonicalUrl?: (url: string) => void;
}) {
  const { data: list, isPending } = useQuery(getUserListQueryOptions(listUrl));

  const canonicalUri = list?.asset_metadata?.list?.uri;
  useEffect(() => {
    if (!onCanonicalUrl || !canonicalUri) return;
    const byDid = listUrlByDid(listUrl, canonicalUri);
    if (byDid !== listUrl) onCanonicalUrl(byDid);
  }, [listUrl, canonicalUri, onCanonicalUrl]);

  return (
    <>
      {list && list.asset_metadata ? (
        <>
          {hasSeparator && <BlockSeparator />}
          {list.asset_metadata.list?.uri && (
            <ListAttribution
              avatar={list.asset_metadata.list.avatar}
              name={list.asset_metadata.list.name}
              creator={list.asset_metadata.list.creator}
            >
              <SimpleTooltip message="Total members">
                <span className="text-slatea-12 dark:text-slatedarka-12 bg-slatea-2 dark:bg-slatedarka-2 rounded-md px-2 py-1 text-xs border border-slatea-5 dark:border-slatedarka-5">
                  {list.asset_metadata.items?.length}
                </span>
              </SimpleTooltip>
            </ListAttribution>
          )}
          <BlockSeparator />
          {list.asset_metadata.items && (
            <AvatarList
              maxVisible={12}
              users={list.asset_metadata.items.map((item) => item.subject)}
              total={list.asset_value.length}
            />
          )}
        </>
      ) : isPending ? (
        <div className="flex justify-center items-center p-2 h-8">
          <Spinner delay={1000} />
        </div>
      ) : (
        <div className="text-sm text-slate-11 dark:text-slatedark-11 p-2">
          This list is not yet in our system. Please verify the list URL and
          wait a few minutes for it to be indexed.
        </div>
      )}
    </>
  );
}
